import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  FormControl,
  FormErrorMessage,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MaybeApiError } from '@resistapp/client/components/states/error';
import { useImportLegacyLabSheet } from '@resistapp/client/hooks/api';
import type { ImportLegacyLabSheetResponse } from '@resistapp/common/api-types';
import { parseSheetIdFromUrl } from '@resistapp/common/utils';
import { useEffect, useMemo, useState } from 'react';

type LegacyLabSheetImportControlProps = {
  workbookId: string;
  organisationId: number;
  onImported?: (response: ImportLegacyLabSheetResponse) => void;
};

export function LegacyLabSheetImportControl({
  workbookId,
  organisationId,
  onImported,
}: LegacyLabSheetImportControlProps) {
  const [sheetUrl, setSheetUrl] = useState('');
  // Linking existing chips was removed; we now error if chips with the URL exist.
  const importer = useImportLegacyLabSheet();
  const [lastSubmittedUrl, setLastSubmittedUrl] = useState<string | null>(null);

  const trimmedSheetUrl = sheetUrl.trim();
  const invalidSheetUrl = useMemo(
    () => trimmedSheetUrl.length > 0 && !parseSheetIdFromUrl(trimmedSheetUrl),
    [trimmedSheetUrl],
  );
  const canSubmit = trimmedSheetUrl.length > 0 && !invalidSheetUrl && !importer.loading;

  // Eager submit as soon as the URL matches the expected pattern, but only once per URL
  const send = importer.send;
  useEffect(() => {
    if (!canSubmit) {
      return;
    }
    if (trimmedSheetUrl === lastSubmittedUrl) {
      return; // do not reattempt automatically after error; wait for user change
    }
    setLastSubmittedUrl(trimmedSheetUrl);
    send({
      sheetUrl: trimmedSheetUrl,
      organisationId,
      labWorkbookId: workbookId,
    });
  }, [canSubmit, send, organisationId, trimmedSheetUrl, workbookId, lastSubmittedUrl]);

  // If workbook/organisation context changes, allow a fresh attempt for the same URL
  useEffect(() => {
    setLastSubmittedUrl(null);
  }, [workbookId, organisationId]);

  useEffect(() => {
    if (importer.data) {
      setSheetUrl('');
      setLastSubmittedUrl(null);
      onImported?.(importer.data);
    }
  }, [importer.data, onImported]);

  return (
    <VStack align="stretch" spacing={3} width="100%">
      <FormControl isInvalid={invalidSheetUrl} isDisabled={importer.loading}>
        <Input
          size="sm"
          value={sheetUrl}
          onChange={event => {
            setSheetUrl(event.target.value);
          }}
          placeholder="Labsheet URL"
        />
        {invalidSheetUrl && <FormErrorMessage>Please provide a valid Google Sheets URL.</FormErrorMessage>}
      </FormControl>

      <MaybeApiError error={importer.error} isAdmin={true} />

      {/* Import triggers automatically when a valid URL is entered */}

      {/* Chip-linking option removed by design. */}

      {importer.data && (
        <Alert status="success" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <VStack align="stretch" spacing={1} width="100%">
            <AlertTitle fontSize="sm">Legacy lab sheet imported</AlertTitle>
            <AlertDescription>
              Linked {importer.data.linkedSampleCount} sample
              {importer.data.linkedSampleCount === 1 ? '' : 's'} for this lab workbook.
            </AlertDescription>
            {Array.isArray(importer.data.warnings) && importer.data.warnings.length > 0 && (
              <VStack align="stretch" spacing={0.5} pt={1} fontSize="xs">
                {importer.data.warnings.map(warning => (
                  <Text key={warning} color="orange.700">
                    • {warning}
                  </Text>
                ))}
              </VStack>
            )}
          </VStack>
        </Alert>
      )}
    </VStack>
  );
}
